import {
  ECadinFederalStatus,
  ECertificadoStatus,
  ESituacaoFiscalSuspensaStatus,
  ESituacaoFiscalDeclaracoesStatus,
  ISituacaoFiscalFederalCadinTableData,
  ISituacaoFiscalFederalCertificadoTableData,
  ESituacaoFiscalStatus,
  ISituacaoFiscalFederalSituacaoFiscalTableData,
  ISituacaoFiscalTableData
} from "@/views/contabil/dashboard/widgets/CadinSituacaoFiscal/types";
import {
  ICertificadoEmpresaResponse,
  IReceitaFederalCadinResponse,
  IReceitaFederalEmpresaResponse,
  IReceitaFederalResponse,
  IReceitaFederalSituacaoFiscalResponse,
  ReceitaFederalCertificadoResponse,
  ReceitaFederalStatusCadin,
  StatusSituacaoFiscalResponse
} from "../requests/types";
import { F360UnitDate, type IF360Date } from "@/core/plugins/date/F360Date";
import { inject, injectable } from "inversify";
import { DITypes } from "@/core/@types/types";
import "reflect-metadata";
import EmpresaMapper from "@/common/contabil/EmpresaMapperAdapter";

const CERTIFICADOS_INVALIDOS = [
  ReceitaFederalCertificadoResponse.Revogado,
  ReceitaFederalCertificadoResponse.CpfDiferente,
  ReceitaFederalCertificadoResponse.Expirado,
  ReceitaFederalCertificadoResponse.DownloadNaoRealizado,
  ReceitaFederalCertificadoResponse.Inapto,
  ReceitaFederalCertificadoResponse.None
];

@injectable()
export default class ReceitaFederal {
  @inject(DITypes.DATE)
  private dateLib!: IF360Date;

  protected response: IReceitaFederalResponse | undefined;

  setResponse(response: IReceitaFederalResponse | undefined) {
    this.response = response;
  }

  handleStatusCadin(
    status: ReceitaFederalStatusCadin | undefined
  ): ECadinFederalStatus {
    if (status === undefined) return ECadinFederalStatus.without;

    const statusMap: Record<ReceitaFederalStatusCadin, ECadinFederalStatus> = {
      [ReceitaFederalStatusCadin.ComDebitos]: ECadinFederalStatus.invalid,
      [ReceitaFederalStatusCadin.ExcluidoPelaRFB]: ECadinFederalStatus.valid,
      [ReceitaFederalStatusCadin.IncluidoNoCadin]: ECadinFederalStatus.invalid,
      [ReceitaFederalStatusCadin.NaoIncluidoNoCadin]: ECadinFederalStatus.valid,
      [ReceitaFederalStatusCadin.None]: ECadinFederalStatus.without,
      [ReceitaFederalStatusCadin.SuspensoPelaRFB]: ECadinFederalStatus.valid,
      [ReceitaFederalStatusCadin.Aviso]: ECadinFederalStatus.pending
    };

    return statusMap[status];
  }

  handleGetCadinLegenda(): Record<ReceitaFederalStatusCadin, string> {
    const cadinMap: Record<ReceitaFederalStatusCadin, string> = {
      [ReceitaFederalStatusCadin.ComDebitos]: "Com débitos",
      [ReceitaFederalStatusCadin.ExcluidoPelaRFB]: "Excluído pela RFB",
      [ReceitaFederalStatusCadin.IncluidoNoCadin]: "Incluído no Cadin",
      [ReceitaFederalStatusCadin.NaoIncluidoNoCadin]: "Não incluído no Cadin",
      [ReceitaFederalStatusCadin.None]: "Nenhuma",
      [ReceitaFederalStatusCadin.SuspensoPelaRFB]: "Suspensa pela RFB",
      [ReceitaFederalStatusCadin.Aviso]: "Aviso"
    };

    return cadinMap;
  }

  handleGetSituacaoFiscalLegenda(): Record<
    StatusSituacaoFiscalResponse,
    string
  > {
    const statusMap: Record<StatusSituacaoFiscalResponse, string> = {
      [StatusSituacaoFiscalResponse.NenhumaPendencia]: "Nenhuma pendência",
      [StatusSituacaoFiscalResponse.None]: "Nenhuma",
      [StatusSituacaoFiscalResponse.Pendencia]: "Pendência",
      [StatusSituacaoFiscalResponse.Aviso]: "Aviso"
    };

    return statusMap;
  }

  handleStatusCadinLegenda(status: ReceitaFederalStatusCadin | null): string {
    if (status === null) return "Não foi possível identificar o status";

    const statusMap = this.handleGetCadinLegenda();

    return statusMap[status];
  }

  handleCadin = (
    cadin: IReceitaFederalCadinResponse | null
  ): ISituacaoFiscalFederalCadinTableData => {
    return {
      consulta: cadin?.data || null,
      relatorio: "",
      status: this.handleStatusCadin(cadin?.status),
      statusValue: cadin?.status || null
    };
  };

  handleSituacaoFiscalStatusLegenda = (
    status: StatusSituacaoFiscalResponse | null
  ): string => {
    if (status === null) return "Não foi possível identificar o status";

    const statusMap: Record<StatusSituacaoFiscalResponse, string> =
      this.handleGetSituacaoFiscalLegenda();

    return statusMap[status];
  };

  handleGetCertificadoLegenda(): Record<
    ReceitaFederalCertificadoResponse,
    string
  > {
    const statusMap: Record<ReceitaFederalCertificadoResponse, string> = {
      [ReceitaFederalCertificadoResponse.CpfDiferente]: "CPF diferente",
      [ReceitaFederalCertificadoResponse.DownloadNaoRealizado]:
        "Download não realizado",
      [ReceitaFederalCertificadoResponse.Expirado]: "Expirado",
      [ReceitaFederalCertificadoResponse.Inapto]: "Inapto",
      [ReceitaFederalCertificadoResponse.None]: "Nenhum",
      [ReceitaFederalCertificadoResponse.Revogado]: "Revogado",
      [ReceitaFederalCertificadoResponse.Valido]: "Válido",
      [ReceitaFederalCertificadoResponse.Vencendo]: "Vencendo"
    };

    return statusMap;
  }

  handleCertificadoStatusLegenda = (
    status: ReceitaFederalCertificadoResponse | null
  ): string => {
    if (status === null) return "Não foi possível identificar o status";

    const statusMap: Record<ReceitaFederalCertificadoResponse, string> =
      this.handleGetCertificadoLegenda();

    return statusMap[status];
  };

  private hasDocumentosPendentesSituacaoFiscal(
    situacaoFiscal: IReceitaFederalSituacaoFiscalResponse | undefined
  ) {
    return (
      situacaoFiscal?.exigibilidadeSuspensa &&
      situacaoFiscal.ausenciaDeDeclaracao
    );
  }

  handleSituacaoFiscalStatus = (
    situacaoFiscal: IReceitaFederalSituacaoFiscalResponse | undefined
  ): ESituacaoFiscalStatus => {
    const statusMap: Record<
      StatusSituacaoFiscalResponse,
      ESituacaoFiscalStatus
    > = {
      [StatusSituacaoFiscalResponse.NenhumaPendencia]:
        ESituacaoFiscalStatus.valid,
      [StatusSituacaoFiscalResponse.None]: ESituacaoFiscalStatus.without,
      [StatusSituacaoFiscalResponse.Pendencia]: ESituacaoFiscalStatus.invalid,
      [StatusSituacaoFiscalResponse.Aviso]: ESituacaoFiscalStatus.pending
    };

    return statusMap[
      situacaoFiscal?.status || StatusSituacaoFiscalResponse.None
    ];
  };

  private handleCertificadoStatus = (
    certificado: ICertificadoEmpresaResponse | null
  ): ECertificadoStatus => {
    if (!certificado?.status) return ECertificadoStatus.error;

    const currentDate = this.dateLib.now();

    const proximos20dias = this.dateLib.add(currentDate, 20, F360UnitDate.Days);

    const isProximoVencer = this.dateLib.isBetween(
      certificado.terminoValidade,
      currentDate,
      proximos20dias,
      F360UnitDate.Days
    );

    if (isProximoVencer) return ECertificadoStatus.venceProximosDias;

    const statusMap: Partial<
      Record<ReceitaFederalCertificadoResponse, ECertificadoStatus>
    > = {
      [ReceitaFederalCertificadoResponse.None]: ECertificadoStatus.error,
      [ReceitaFederalCertificadoResponse.Valido]: ECertificadoStatus.valid,
      [ReceitaFederalCertificadoResponse.Expirado]: ECertificadoStatus.expirado
    };

    const statusvalue = statusMap[certificado?.status];

    if (statusvalue === undefined) return ECertificadoStatus.pending;

    return statusvalue;
  };

  handleSituacaoFiscalSuspensaStatus = (
    exigibilidadeSuspensa: boolean | undefined
  ): ESituacaoFiscalSuspensaStatus => {
    if (!exigibilidadeSuspensa) return ESituacaoFiscalSuspensaStatus.error;

    if (exigibilidadeSuspensa) return ESituacaoFiscalSuspensaStatus.valid;

    return ESituacaoFiscalSuspensaStatus.pending;
  };

  handleSituacaoFiscal = (
    situacaoFiscal: IReceitaFederalSituacaoFiscalResponse | null
  ): ISituacaoFiscalFederalSituacaoFiscalTableData => {
    return {
      consulta: situacaoFiscal?.dataConsulta || null,
      debFiscal: situacaoFiscal?.debitoTotalFiscal.toString() || null,
      status: this.handleSituacaoFiscalStatus(situacaoFiscal || undefined),
      statusValue: situacaoFiscal?.status || null,
      suspensa: this.handleSituacaoFiscalSuspensaStatus(
        situacaoFiscal?.exigibilidadeSuspensa
      ),
      relatorio: null,
      debPrevidenciario:
        situacaoFiscal?.debitoTotalPrevidenciario.toString() || null,
      declaracoes: ESituacaoFiscalDeclaracoesStatus.valid,
      ausenciaDeclaracao: situacaoFiscal?.ausenciaDeDeclaracao || false,
      exigibilidadeSuspensa: situacaoFiscal?.exigibilidadeSuspensa || false
    };
  };

  handleCertificado = (
    certificado: ICertificadoEmpresaResponse | null
  ): ISituacaoFiscalFederalCertificadoTableData => {
    return {
      validade: certificado?.terminoValidade || null,
      status: this.handleCertificadoStatus(certificado),
      statusValue: certificado?.status || ReceitaFederalCertificadoResponse.None
    };
  };

  private handleEmpresasPendentesSituacao = (
    empresas: IReceitaFederalEmpresaResponse[]
  ) =>
    empresas.filter(
      (empresa) =>
        this.handleSituacaoFiscal(empresa.situacaoFiscal).status ===
        ESituacaoFiscalStatus.invalid
    );

  private empresasComAviso = (empresas: IReceitaFederalEmpresaResponse[]) =>
    empresas.filter(
      (empresa) => empresa.cadin?.status === ReceitaFederalStatusCadin.Aviso
    );

  handlePendenciaCadinAlert = (empresaId?: string) => {
    const empresasComPendencia = this.empresasComAviso(
      this.response?.empresas || []
    );

    if (empresaId)
      return empresasComPendencia.filter((empresa) => empresa.id === empresaId);

    return empresasComPendencia;
  };

  handlePendenciaSituacaoFiscal = (empresaId?: string) => {
    if (!empresaId) {
      const empresasComPendencias = this.handleEmpresasPendentesSituacao(
        this.response?.empresas || []
      );

      return empresasComPendencias;
    }

    return this.handleEmpresasPendentesSituacao(
      this.response?.empresas.filter((empresa) => empresa.id === empresaId) ||
        []
    );
  };

  handleAusenciaDeclaracaoSituacaoFiscalAlert = (empresaId?: string) => {
    const empresasAusenteDeclaracao =
      this.response?.empresas.filter(
        (empresa) => empresa.situacaoFiscal?.ausenciaDeDeclaracao
      ) || [];

    // Se empresaId não for fornecido, retorna para todas as empresas
    if (!empresaId) {
      return empresasAusenteDeclaracao;
    }

    // Filtra apenas para a empresa com o id especificado (empresaId)
    return empresasAusenteDeclaracao.filter(
      (empresa) => empresa.id === empresaId
    );
  };

  handleEmpresasComMensagensImportantes(): IReceitaFederalEmpresaResponse[] {
    return (
      this.response?.empresas.filter(
        (empresa) => empresa.situacaoFiscal?.alertaMensagemCaixaPostal
      ) || []
    );
  }

  handleCertificadoAlert(empresaId?: string): IReceitaFederalEmpresaResponse[] {
    const empresasInvalidas = this.response?.empresas.filter((empresa) =>
      CERTIFICADOS_INVALIDOS.includes(
        empresa.certificadoDeEmpresa?.status ||
          ReceitaFederalCertificadoResponse.None
      )
    );

    if (empresaId)
      return (
        empresasInvalidas?.filter((empresa) => empresa.id === empresaId) || []
      );

    return empresasInvalidas || [];
  }

  formatToTable = (): ISituacaoFiscalTableData[] => {
    if (!this.response) return [];

    return this.response.empresas.map((empresa) => ({
      empresa: EmpresaMapper.toEntity(empresa),
      certificado: this.handleCertificado(empresa.certificadoDeEmpresa),
      cadinFederal: this.handleCadin(empresa.cadin),
      situacaoFiscal: this.handleSituacaoFiscal(empresa.situacaoFiscal),
      situacaoFiscalFile: empresa.situacaoFiscalFile || null,
      cadinFile: empresa.cadinFile || null,
      id: empresa.id
    }));
  };
}
